<template>
  <div
    class="statistics-cards-container"
    :class="{
      'statistics-cards-container--loading': loading,
      'statistics-cards-container--with-gift': !loading && isGiftButtonActive,
    }"
  >
    <template v-if="loading">
      <div v-for="n in 4" :key="'skeleton_card_' + n" class="stat-card-wrapper">
        <div class="stat-card stat-card--static">
          <div class="stat-card-body">
            <div class="skeleton skeleton-icon"></div>
            <div class="stat-card-copy">
              <div class="skeleton skeleton-value"></div>
              <div class="skeleton skeleton-label"></div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="stat-card-wrapper">
        <router-link
          :to="{ name: 'user.my-assignment', query: { ...$route.query, tab: 'with_expired', val: false } }"
          class="stat-card"
        >
          <div class="stat-card-body">
            <span class="stat-icon" aria-hidden="true">
              <i class="uil-book-open"></i>
            </span>
            <div class="stat-card-copy">
              <span class="stat-value">{{ dashboardItem.assigned_educations_count || 0 }}</span>
              <span class="stat-label">{{ $t("home.education_assigned") }}</span>
            </div>
          </div>
        </router-link>
      </div>

      <div class="stat-card-wrapper">
        <router-link
          :to="{ name: 'user.my-assignment', query: { ...$route.query, tab: 'completed', val: true } }"
          class="stat-card"
        >
          <div class="stat-card-body">
            <span class="stat-icon" aria-hidden="true">
              <i class="uil-presentation-check"></i>
            </span>
            <div class="stat-card-copy">
              <span class="stat-value">{{ dashboardItem.completed_educations_count || 0 }}</span>
              <span class="stat-label">{{ $t("home.i_completed_education") }}</span>
            </div>
          </div>
        </router-link>
      </div>

      <div v-if="isGiftButtonActive" class="stat-card-wrapper">
        <router-link to="gift" class="stat-card">
          <div class="stat-card-body">
            <span class="stat-icon" aria-hidden="true">
              <i class="uil-gift"></i>
            </span>
            <div class="stat-card-copy">
              <span class="stat-value">{{ dashboardItem.gifts_count || 0 }}</span>
              <span class="stat-label">{{ $t("home.i_won_a_gift") }}</span>
            </div>
          </div>
        </router-link>
      </div>

      <div class="stat-card-wrapper">
        <div class="stat-card stat-card--static">
          <div class="stat-card-body">
            <span class="stat-icon" aria-hidden="true">
              <i class="uil-coins"></i>
            </span>
            <div class="stat-card-copy">
              <span class="stat-value stat-value--split">
                <span>{{ dashboardItem.points_count || 0 }}</span>
                <span class="stat-value-sep">/</span>
                <span>{{ dashboardItem.coins_count || 0 }}</span>
              </span>
              <span class="stat-label">{{ $t("home.point_coin") }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: "StatisticsCards",
  props: {
    dashboardItem: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isGiftButtonActive: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style scoped>
.skeleton {
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.skeleton-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  flex-shrink: 0;
}

.skeleton-value {
  width: 52px;
  height: 24px;
  margin-bottom: 8px;
}

.skeleton-label {
  width: 96px;
  height: 12px;
}

.statistics-cards-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-bottom: 8px;
  width: 100%;
  align-items: stretch;
}

.statistics-cards-container--with-gift,
.statistics-cards-container--loading {
  grid-template-columns: repeat(4, 1fr);
}

.stat-card-wrapper {
  display: flex;
  min-width: 0;
  height: 100%;
}

.stat-card {
  display: flex;
  flex: 1;
  width: 100%;
  min-height: 88px;
  text-decoration: none;
  color: inherit;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px !important;
  box-shadow: none !important;
  transition: border-color 0.15s ease;
  cursor: pointer;
  overflow: hidden;
}

.stat-card:hover {
  border-color: #cbd5e1;
  text-decoration: none;
  color: inherit;
}

.stat-card--static {
  cursor: default;
}

.stat-card--static:hover {
  border-color: #e2e8f0;
}

.stat-card-body {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 16px 18px;
}

.stat-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: transparent;
  color: #64748b;
  flex-shrink: 0;
}

.stat-icon i {
  font-size: 18px;
  line-height: 1;
}

.stat-card-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.stat-value {
  display: block;
  font-family: var(--font-secondary, inherit);
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}

.stat-value--split {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 1.375rem;
}

.stat-value-sep {
  color: #94a3b8;
  font-weight: 500;
}

.stat-label {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin: 0;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.35;
  color: #64748b;
}

@media (max-width: 1024px) {
  .statistics-cards-container,
  .statistics-cards-container--with-gift,
  .statistics-cards-container--loading {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .stat-card {
    min-height: 84px;
  }

  .stat-value {
    font-size: 1.375rem;
  }

  .stat-value--split {
    font-size: 1.25rem;
  }
}

@media (max-width: 576px) {
  .statistics-cards-container,
  .statistics-cards-container--with-gift,
  .statistics-cards-container--loading {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .stat-card {
    min-height: 76px;
  }

  .stat-card:hover {
    border-color: #e2e8f0;
  }

  .stat-card-body {
    padding: 14px 12px;
    gap: 10px;
  }

  .stat-icon {
    width: 36px;
    height: 36px;
    border-radius: 9px;
  }

  .stat-icon i {
    font-size: 16px;
  }

  .stat-value {
    font-size: 1.25rem;
  }

  .stat-value--split {
    font-size: 1.125rem;
    gap: 4px;
  }

  .stat-label {
    font-size: 0.6875rem;
  }

  .stat-card-wrapper:last-child:nth-child(odd) {
    grid-column: 1 / -1;
  }
}

@media (max-width: 400px) {
  .stat-card-body {
    padding: 12px 10px;
  }

  .stat-value {
    font-size: 1.125rem;
  }

  .stat-value--split {
    font-size: 1rem;
  }
}

@media (max-width: 576px) {
  .stat-card {
    min-height: 88px;
  }

  .stat-card-body {
    min-height: 88px;
    padding: 14px 12px;
    gap: 10px;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    min-width: 40px;
  }

  .stat-value {
    font-size: 20px;
    line-height: 1.25;
  }

  .stat-value--split {
    font-size: 18px;
  }

  .stat-label {
    font-size: 14px;
    line-height: 1.4;
  }
}
</style>
